import { useMemo } from 'react'
import { toast } from 'sonner'
import { useAppSelector } from '@/store/hooks'
import { selectJobByCourse, isStartedTranslationRun } from '@/features/ai/jobsSlice'
import { useCourseJob } from '@/features/ai/useCourseJob'
import { translationRunView } from '@/features/ai/lib/translationRun'
import { BLOCK_WRITING_JOB_TYPES } from '@/features/ai/studioTypes'
import { useIsCourseUnderReview } from '@/features/moderation/useCourseModeration'
import { resolveAlias, type IdAliases } from '@/features/editor/lib/idAliases'
import { LOCK_REASON_TEXT } from './lockReasonText'

/**
 * Честное состояние «какие блоки курса прямо сейчас заперты».
 * `'ids'` — известен точный список (правка блоков присылает его в теле
 * старта); `'all'` — заперты гарантированно ВСЕ блоки курса. Это НЕ то же
 * самое, что «список пуст», и код обязан отличать одно от другого.
 *
 * `'all'` больше НЕ однозначно «перевод» — причина едет явно: у перевода
 * список блоков неизвестен (§7.5), у проверки курса (`review`) бэк отбивает
 * любую запись в контент, пока ревью `pending`.
 */
export type BlocksInRun =
  | { kind: 'none' }
  | { kind: 'ids'; ids: ReadonlySet<number> }
  | { kind: 'all'; reason: 'translation' | 'review' }

// Стабильные ссылки: потребители кладут состояние в зависимости эффектов и в
// реф гейтов (`useBlockUploadCommit`) — новый объект на рендер их дёргал бы.
const NONE: BlocksInRun = { kind: 'none' }
const TRANSLATION: BlocksInRun = { kind: 'all', reason: 'translation' }
const REVIEW: BlocksInRun = { kind: 'all', reason: 'review' }

/** Занят ли конкретный блок — единственное место, где `BlocksInRun` читают
 *  точечно; потребитель не обязан знать про `kind: 'all'` отдельно. */
export const isBlockLocked = (state: BlocksInRun, blockId: number): boolean =>
  state.kind === 'all' || (state.kind === 'ids' && state.ids.has(blockId))

/** id блоков прогона плюс их текущие id: прогон стартовал по id прежнего
 *  источника (живого курса), а редактор мог уже перечитать курс из черновика. */
const runBlockIds = (ids: readonly number[], aliases: IdAliases): ReadonlySet<number> =>
  new Set([...ids, ...ids.map((id) => resolveAlias(aliases, 'block', id))])

export type BlockLockReason = 'edit' | 'translation' | 'review'

/**
 * Чем блок занят — честный глагол для всех мест, где это видно (плашка панели,
 * шапка панели, чип и тултип миниатюры, отказ фонового коммита картинки).
 * Тексты — `lockReasonText.ts`, словарь исчерпывающий по `BlockLockReason`.
 */
export const lockReasonOf = (state: BlocksInRun): BlockLockReason =>
  state.kind === 'all' ? state.reason : 'edit'

/**
 * Страховка хендлера записи блока (`useBlockDraft.doSave`/`doDelete`): кнопки
 * гаснут по тому же `isBlockLocked`, но клик мог уйти за миг до того, как
 * опрос принёс `pending` (или прогон занял блок), а `PUT` блока — полная
 * замена. `true` — отказано, причина уже показана (текст тултипа шапки панели).
 */
export const refusedByBlockLock = (state: BlocksInRun, blockId: number): boolean => {
  if (!isBlockLocked(state, blockId)) return false
  toast.error(LOCK_REASON_TEXT[lockReasonOf(state)].headerTitle)
  return true
}

/**
 * Блоки, которые прямо сейчас переписывает прогон, пишущий в блоки (правка
 * ИЛИ перевод). Контракт (§6.2, §7.5) требует блокировать ИХ РЕДАКТИРОВАНИЕ,
 * а не только кнопку запуска: воркфлоу взял снимок на старте, и добавленный
 * автором элемент/вариант/кнопка (или переписанный переводом текст) исчезнут
 * при записи патча.
 *
 * Правка блоков считается по `entry.params` (джоба списка блоков не
 * возвращает, §1.2) — известны точные id. У перевода списка блоков нет
 * вовсе — заняты ВСЕ блоки курса (`kind: 'all'`), у картинок цели знает
 * только сервер — точечный гейт для них по-прежнему невозможен, `useBlocksInRun`
 * для них честно отдаёт `'none'` (их отсекает грубый `useIsBlockWritingRunActive`).
 *
 * Идущая проверка курса (`review`) запирает ВСЕ блоки поверх любого прогона
 * (`REVIEW`, см. ниже) — она вторая причина того же лока, а не отдельный гейт:
 * так гаснут разом все пути записи, уже читающие этот хук.
 *
 * Гейт на `isPolling`, а НЕ на наличие записи: терминальная запись живёт до
 * `detach` (её показывает итог), и блокировка по ней оставила бы блоки
 * мёртвыми до закрытия карточки итога.
 *
 * `isStatusUnknown` тоже в гейте: запись реестра могла пролежать в persist
 * (после перезагрузки страницы) — `isPolling` в этот момент уже истинен, а
 * первый ответ опроса ещё не пришёл. Без этой проверки плашка «Блок
 * переписывается прогоном» могла бы соврать про давно завершённый прогон.
 */
export const useBlocksInRun = (courseId: number): BlocksInRun => {
  const { entry, job, isPolling, isStatusUnknown } = useCourseJob(courseId)
  // Тот же вид, что рисует полосу в меню: перевод идёт, пока идёт полоса. С картинками это две
  // джобы, и по `entry.type` блокировка снималась на стыке, посреди перевода.
  const translating = translationRunView(entry, job).kind === 'running'
  const underReview = useIsCourseUnderReview(courseId)
  const params = entry?.params
  const aliases = useAppSelector((s) => s.editor.idAliases)
  return useMemo((): BlocksInRun => {
    // ПЕРВЫМ: проверка важнее прогона — при ней бэк отбивает ЛЮБУЮ запись,
    // а не только в блоки, которые переписывает прогон.
    if (underReview) return REVIEW
    if (translating) return TRANSLATION
    if (!isPolling || isStatusUnknown) return NONE
    if (params?.kind === 'course_block_edit')
      return { kind: 'ids', ids: runBlockIds(params.block_ids, aliases) }
    return NONE
  }, [underReview, translating, isPolling, isStatusUnknown, params, aliases])
}

/**
 * Task 10 (Б, §6.2): любой прогон, ПИШУЩИЙ в блоки (правка ИЛИ картинки),
 * активен прямо сейчас — грубее, чем `useBlocksInRun`. У прогона картинок
 * цели считает СЕРВЕР, клиенту id неизвестны, точечный гейт по блокам для
 * него невозможен: гейт реордера читает этот флаг и отсекает операцию
 * ЦЕЛИКОМ, а не по пересечению диапазона с известными id.
 */
export const useIsBlockWritingRunActive = (courseId: number): boolean => {
  const { entry, isPolling, isStatusUnknown } = useCourseJob(courseId)
  // Проверка курса гасит тот же грубый гейт (реордер, оформление урока): пока
  // ревью `pending`, бэк отбивает запись в любой блок.
  const underReview = useIsCourseUnderReview(courseId)
  const runActive =
    isPolling && !isStatusUnknown && entry !== null && BLOCK_WRITING_JOB_TYPES.has(entry.type)
  return underReview || runActive
}

/**
 * Текст курса заперт на всём протяжении цепочки перевода, включая картиночную
 * стадию (так закрепляет `useBlocksInRun.translation.test.tsx`: «картинки
 * перевода идут — все блоки и текст заперты»), ИЛИ идёт проверка курса: при
 * ней бэк отбивает запись сам. Отдельный прогон картинок без перевода текст
 * не запирает. Отдельный предикат, а не `useIsBlockWritingRunActive`: тот
 * истинен и на таком прогоне картинок.
 */
export const useIsCourseTextLocked = (courseId: number): boolean => {
  const { entry, job } = useCourseJob(courseId)
  const underReview = useIsCourseUnderReview(courseId)
  return underReview || translationRunView(entry, job).kind === 'running'
}

/**
 * Прогон перевода по этому курсу ИДЁТ сейчас — запись реестра про перевод И
 * опрос ещё не видел терминального статуса. Строку перевода бэк заводит той
 * же транзакцией, что и джобу (§7.1), а деталь курса до терминального статуса
 * не перечитывается: пока прогон в полёте, строку видно только через реестр.
 *
 * Гейт на `isPolling` обязателен. Терминальная запись живёт в реестре (и в
 * persist) бессрочно, а `JobPoller` на терминальном статусе уже инвалидировал
 * `Course` — дальше точный ответ даёт `course.translations`. Прежний смысл
 * «когда-либо стартовал» держал удалённый язык в меню и корзину над ним:
 * после успешного `DELETE` клик по ней уходил в `404`.
 *
 * Запись цепочки (§7.4) к моменту картинок уже перецеплена — «про перевод»
 * читается по явному `entry.translationChain` (`isStartedTranslationRun`), а
 * не по `previous.type`: продолжение прерванной картиночной половины (§8)
 * перезаписывает `previous` снимком картиночного родителя.
 */
export const useHasStartedTranslationRun = (courseId: number): boolean => {
  const entry = useAppSelector((s) => selectJobByCourse(s, courseId))
  const { isPolling } = useCourseJob(courseId)
  return isStartedTranslationRun(entry) && isPolling
}

/**
 * Свой (привязанный) прогон курса ещё в полёте. Нужен платным кнопкам запуска:
 * смета узнаёт о только что стартовавшей джобе не сразу (тег `Quote` протухает
 * лишь на терминальном статусе), и сразу после `201` `quote.active_job` пуст —
 * второй клик уходил бы в `409`.
 */
export const useIsOwnRunActive = (courseId: number): boolean => useCourseJob(courseId).isPolling
